/**
 * Analytics utility — thin wrapper over Plausible + Microsoft Clarity.
 *
 * Plausible: fires window.plausible(eventName, { props }) — provided by /js/script.js
 * Clarity:   fires window.clarity('event', eventName) — provided by clarity.ms/tag
 *
 * All functions are fire-and-forget and never throw.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    plausible?: (event: string, opts?: { props?: Record<string, string | number | boolean> }) => void;
    clarity?: (action: string, ...args: any[]) => void;
    __CLARITY_ID__?: string;
  }
}

function plausible(event: string, props?: Record<string, string | number | boolean>): void {
  try {
    window.plausible?.(event, props ? { props } : undefined);
  } catch {
    // silently ignore
  }
}

function clarity(event: string): void {
  try {
    window.clarity?.('event', event);
  } catch {
    // silently ignore
  }
}

function track(event: string, props?: Record<string, string | number | boolean>): void {
  plausible(event, props);
  clarity(event);
}

// ── Game events ────────────────────────────────────────────────────────────

export function trackGameStarted(scenario: string, mode: 'ai' | 'pvp'): void {
  track('game_started', { scenario, mode });
}

export function trackGameCompleted(opts: {
  scenario: string;
  mode: 'ai' | 'pvp';
  result: 'win' | 'loss' | 'draw';
  turns: number;
}): void {
  track('game_completed', {
    scenario: opts.scenario,
    mode: opts.mode,
    result: opts.result,
    turns: opts.turns,
  });
}

// ── Blog / Learn events ───────────────────────────────────────────────────

export function trackArticleRead(slug: string, category: string): void {
  track('article_read', { slug, category });
}

export function trackLearnArticleRead(slug: string): void {
  track('learn_article_read', { slug });
}

// ── Agent / API events ────────────────────────────────────────────────────

export function trackAgentRegistered(): void {
  track('agent_registered');
}

// ── Auth events ───────────────────────────────────────────────────────────

export function trackSignUp(): void {
  track('sign_up');
}

// ── Core Web Vitals (RUM) ─────────────────────────────────────────────────

export function trackWebVital(metric: {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  id: string;
}): void {
  // LCP/FCP/TTFB are in ms; CLS is unitless (multiply by 1000 for Plausible readability); INP is in ms
  const value = metric.name === 'CLS' ? Math.round(metric.value * 1000) : Math.round(metric.value);
  track('web_vital', {
    metric: metric.name,
    value,
    rating: metric.rating,
  });
}
